import React, { useState } from 'react';
import { theme } from '../styles/theme';
import Button from './ui/Button';

interface OnboardingQuestionnaireProps {
  isOpen: boolean;
  onComplete: (data: {
    howHeardAboutUs: string;
    platformUseCase: string;
    additionalDetails: string;
  }) => void;
}

const OnboardingQuestionnaire: React.FC<OnboardingQuestionnaireProps> = ({
  isOpen,
  onComplete
}) => {
  const [howHeardAboutUs, setHowHeardAboutUs] = useState('');
  const [platformUseCase, setPlatformUseCase] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (howHeardAboutUs && platformUseCase) {
      onComplete({
        howHeardAboutUs,
        platformUseCase,
        additionalDetails
      });
    }
  };

  const hearAboutUsOptions = [
    'Social Media',
    'Search Engine',
    'Friend/Colleague',
    'Advertisement',
    'Blog/Article',
    'Other'
  ];

  const useCaseOptions = [
    'Track sales conversions',
    'Monitor affiliate links',
    'Analyze marketing campaigns',
    'Creator/influencer tracking',
    'E-commerce analytics',
    'Other'
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: theme.colors.primary + '20',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '40px'
          }}>
            👋
          </div>
          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: theme.typography.sizes['2xl'],
            fontWeight: theme.typography.weights.bold,
            color: theme.colors.text.primary
          }}>
            Welcome to Revenue Social!
          </h2>
          <p style={{
            margin: 0,
            fontSize: theme.typography.sizes.base,
            color: theme.colors.text.secondary,
            lineHeight: 1.6
          }}>
            Help us personalize your experience by answering a few quick questions.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Question 1 */}
          <div style={{ marginBottom: '28px' }}>
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontSize: theme.typography.sizes.base,
              fontWeight: theme.typography.weights.semibold,
              color: theme.colors.text.primary
            }}>
              How did you hear about us? <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <div style={{ display: 'grid', gap: '8px' }}>
              {hearAboutUsOptions.map((option) => (
                <label
                  key={option}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    border: `2px solid ${howHeardAboutUs === option ? theme.colors.primary : theme.colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: howHeardAboutUs === option ? theme.colors.primary + '10' : 'white'
                  }}
                  onMouseEnter={(e) => {
                    if (howHeardAboutUs !== option) {
                      e.currentTarget.style.borderColor = theme.colors.primary + '60';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (howHeardAboutUs !== option) {
                      e.currentTarget.style.borderColor = theme.colors.border;
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="howHeardAboutUs"
                    value={option}
                    checked={howHeardAboutUs === option}
                    onChange={(e) => setHowHeardAboutUs(e.target.value)}
                    style={{ marginRight: '12px', accentColor: theme.colors.primary }}
                  />
                  <span style={{
                    fontSize: theme.typography.sizes.base,
                    color: theme.colors.text.primary
                  }}>
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Question 2 */}
          <div style={{ marginBottom: '28px' }}>
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontSize: theme.typography.sizes.base,
              fontWeight: theme.typography.weights.semibold,
              color: theme.colors.text.primary
            }}>
              What do you plan on using the platform for? <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <div style={{ display: 'grid', gap: '8px' }}>
              {useCaseOptions.map((option) => (
                <label
                  key={option}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    border: `2px solid ${platformUseCase === option ? theme.colors.primary : theme.colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: platformUseCase === option ? theme.colors.primary + '10' : 'white'
                  }}
                  onMouseEnter={(e) => {
                    if (platformUseCase !== option) {
                      e.currentTarget.style.borderColor = theme.colors.primary + '60';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (platformUseCase !== option) {
                      e.currentTarget.style.borderColor = theme.colors.border;
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="platformUseCase"
                    value={option}
                    checked={platformUseCase === option}
                    onChange={(e) => setPlatformUseCase(e.target.value)}
                    style={{ marginRight: '12px', accentColor: theme.colors.primary }}
                  />
                  <span style={{
                    fontSize: theme.typography.sizes.base,
                    color: theme.colors.text.primary
                  }}>
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'block',
              marginBottom: '12px',
              fontSize: theme.typography.sizes.base,
              fontWeight: theme.typography.weights.semibold,
              color: theme.colors.text.primary
            }}>
              Tell us more about your use case (optional)
            </label>
            <textarea
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
              placeholder="Share any additional details about how you plan to use Revenue Social..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                fontSize: theme.typography.sizes.base,
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={!howHeardAboutUs || !platformUseCase}
            style={{ width: '100%' }}
          >
            Continue to Setup
          </Button>
        </form>
      </div>
    </div>
  );
};

export default OnboardingQuestionnaire;
